import {useState, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import { FiUserCheck } from "react-icons/fi";
import { getAdmins, impersonate } from "../../services/authApi";
import useAuthStore from "../../states/authStore";
import styles from "./Troubleshoot.module.css";

function formatValue(value) {
    if (value === null || value === undefined || value === "") return "Not provided";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function isDisplayField(field) {
    return !["first_name", "last_name"].includes(field)
        && !/password|token|secret|security.*answer|answer.*security/i.test(field);
}

function Troubleshoot() {
    const [admins, setAdmins] = useState([]);
    const [pendingAdmin, setPendingAdmin] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const revalidateSession = useAuthStore((state) => state.revalidateSession);
    const navigate = useNavigate();

    async function handleImpersonate(admin) {
        if (pendingAdmin !== null) return;
        setPendingAdmin(admin);
        setFeedback(null);
        try {
            await impersonate(admin);
            const authenticated = await revalidateSession();
            if (!authenticated) throw new Error("Impersonation started, but the session could not be refreshed.");
            setFeedback({ message: `Now impersonating ${admin.email || "the selected admin"}.`, error: false });
        } catch (error) {
            setFeedback({ message: error.message || "Failed to start impersonation", error: true });
        } finally {
            setPendingAdmin(null);
            navigate("/");
        }
    }

    useEffect(() => {
        async function fetchAdmins() {
            try {
                const data = await getAdmins();
                setAdmins(data);
            } catch (error) {
                console.error("Failed to fetch admins:", error);
            }
        }
        fetchAdmins();
    }, []);

    return (
        <div className={styles.page}>
            <h1>Clients Troubleshooting Station</h1>
            {feedback && <p role={feedback.error ? "alert" : "status"}>{feedback.message}</p>}
            <ul className={styles.grid}>
                {admins.map((admin) => (
                    <li className={styles.widget} key={admin.id ?? admin.email}>
                        <h2 className={styles.name}>
                            {[admin.first_name, admin.last_name].filter(Boolean).join(" ") || admin.email}
                        </h2>
                        <dl className={styles.details}>
                            {Object.entries(admin).filter(([field]) => isDisplayField(field)).map(([field, value]) => (
                                <div className={styles.field} key={field}>
                                    <dt>{field.replace(/_/g, " ")}</dt>
                                    <dd>
                                        {field === "email" && value ? (
                                            <a href={`mailto:${value}`}>{value}</a>
                                        ) : formatValue(value)}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        <button
                            type="button"
                            className={styles.impersonateButton}
                            disabled={pendingAdmin !== null || admin.user_id == null || admin.organization_id == null}
                            onClick={() => handleImpersonate(admin)}
                            title={admin.user_id == null || admin.organization_id == null
                                ? "User ID and organization ID are required to impersonate this admin"
                                : `Impersonate ${admin.email || "this admin"}`}
                        >
                            <FiUserCheck aria-hidden="true" />
                            {pendingAdmin === admin ? "Starting..." : "Impersonate"}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default Troubleshoot;    
